import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useBranding, MADLO_DEFAULT, applyBranding } from "@/context/BrandingContext";
import BrandLogo from "@/components/BrandLogo";
import { Palette, Save, Upload, RotateCcw, Loader2 } from "lucide-react";

const COLORS = [
  ["couleur_primaire", "Couleur principale (boutons, titres, bandeaux PDF)"],
  ["couleur_survol", "Couleur au survol"],
  ["couleur_fonce", "Couleur foncée (texte, fond de connexion)"],
  ["couleur_texte_secondaire", "Texte secondaire / libellés"],
  ["couleur_filet", "Filets et bordures"],
];

export default function BrandingCard() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const favRef = useRef(null);

  useEffect(() => { setForm({ ...branding }); }, [branding]);
  if (!form) return null;

  const set = (k, v) => { const next = { ...form, [k]: v }; setForm(next); if (k.startsWith("couleur_") || k === "police" || k === "nom_application") applyBranding(next); };

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/branding", form); await refresh(); setForm(null); toast.success("Identité visuelle enregistrée"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); applyBranding(branding); }
    finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Rétablir le thème MADLO ? Le logo importé et les couleurs de la gérance seront retirés.")) return;
    setBusy(true);
    try { await api.post("/settings/branding/reset"); await refresh(); setForm(null); toast.success("Thème MADLO rétabli"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/settings/logo", fd);
      await refresh(); setForm(null); toast.success("Logo mis à jour");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const uploadFavicon = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/settings/favicon", fd);
      await refresh(); setForm(null); toast.success("Favicon mis à jour");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (favRef.current) favRef.current.value = ""; }
  };
  const removeFavicon = async () => {
    setBusy(true);
    try { await api.delete("/settings/favicon"); await refresh(); setForm(null); toast.success("Favicon retiré"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-dd-light p-6" data-testid="branding-card">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-dd-blue/10 flex items-center justify-center shrink-0"><Palette className="text-dd-blue" size={22} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-dd-blue">Identité visuelle de la gérance</h3>
          <p className="text-sm text-dd-warm mt-1">Logo, couleurs et police de l'application et des PDF. Par défaut : thème MADLO Constat.</p>

          <div className="mt-4 flex flex-wrap items-center gap-6">
            <div className="rounded-md border border-dd-light p-4 bg-white"><BrandLogo className="h-12" /></div>
            <div className="rounded-md p-4 bg-dd-night"><BrandLogo className="h-12" negative /></div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" data-testid="logo-input" onChange={(e) => upload(e.target.files?.[0])} />
              <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="h-11 border-dd-blue text-dd-blue" data-testid="logo-upload-btn">
                {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Upload size={16} className="mr-1" />} Importer un logo
              </Button>
              <label className="flex items-center gap-2 mt-2 text-sm text-dd-night cursor-pointer">
                <Checkbox checked={!!form.logo_fond_blanc} onCheckedChange={(v) => set("logo_fond_blanc", !!v)} data-testid="logo-fond-blanc" />
                Afficher le logo sur une pastille blanche (fond sombre)
              </label>
            </div>
            <div className="flex items-center gap-3 pl-6 border-l border-dd-light" data-testid="favicon-block">
              <div className="w-11 h-11 rounded-md border border-dd-light bg-white flex items-center justify-center overflow-hidden">
                <img src={branding.favicon_url || "/favicon.png"} alt="favicon" className="w-8 h-8 object-contain" data-testid="favicon-preview" />
              </div>
              <div>
                <input ref={favRef} type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml,image/webp,image/jpeg" className="hidden" data-testid="favicon-input" onChange={(e) => uploadFavicon(e.target.files?.[0])} />
                <Button variant="outline" disabled={busy} onClick={() => favRef.current?.click()} className="h-11 border-dd-blue text-dd-blue" data-testid="favicon-upload-btn">
                  <Upload size={16} className="mr-1" /> Importer un favicon
                </Button>
                <div className="text-xs text-dd-warm mt-1">Icône de l'onglet du navigateur (PNG/ICO/SVG carré, 512 Ko max).
                  {branding.favicon_file_id && <button onClick={removeFavicon} className="ml-2 text-destructive underline" data-testid="favicon-remove-btn">Retirer</button>}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
            <div>
              <Label className="text-dd-night text-sm">Nom de l'application</Label>
              <Input value={form.nom_application} onChange={(e) => set("nom_application", e.target.value)} className="h-11 mt-1" data-testid="brand-nom" />
            </div>
            <div>
              <Label className="text-dd-night text-sm">Police</Label>
              <Select value={form.police} onValueChange={(v) => set("police", v)}>
                <SelectTrigger className="h-11 mt-1" data-testid="brand-police"><SelectValue /></SelectTrigger>
                <SelectContent>{["Inter", "Poppins", "Montserrat"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {COLORS.map(([k, label]) => (
              <div key={k}>
                <Label className="text-dd-night text-sm">{label}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11 w-14 rounded-md border border-dd-light cursor-pointer" data-testid={`brand-color-${k}`} />
                  <Input value={form[k]} onChange={(e) => set(k, e.target.value)} className="h-11 font-mono" data-testid={`brand-hex-${k}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <Button onClick={save} disabled={busy} className="h-11 bg-dd-blue hover:bg-dd-hover text-white" data-testid="brand-save"><Save size={16} className="mr-1" /> Enregistrer</Button>
            <Button variant="outline" onClick={() => { setForm({ ...branding }); applyBranding(branding); }} className="h-11 border-dd-light" data-testid="brand-cancel">Annuler les modifications</Button>
            <Button variant="outline" onClick={reset} disabled={busy} className="h-11 border-dd-light text-dd-night" data-testid="brand-reset"><RotateCcw size={16} className="mr-1" /> Rétablir le thème MADLO</Button>
          </div>
          <p className="text-xs text-dd-warm mt-3">Le logo importé remplace le logotype MADLO dans l'application et dans l'en-tête des PDF. Formats : PNG, JPEG, SVG, WebP (2 Mo max). Les valeurs par défaut MADLO : {MADLO_DEFAULT.couleur_primaire} / {MADLO_DEFAULT.police}.</p>
        </div>
      </div>
    </div>
  );
}
